/**
 * Industry Theme Generator Component
 * Parent component that orchestrates the entire theme generation flow.
 *
 * Wizard steps (industry-standard WordPress theme builder):
 *  1. Industry   – Choose your business industry + optional specialization
 *  2. Branding   – Pick brand colors and typography
 *  3. Layout     – Select the homepage layout style
 *  4. Templates  – Choose which theme template files to include
 *  5. Customize  – Per-template layout presets and modular section configuration
 *  6. Review & Generate – Name your theme, pick animation, use profile, then generate
 *
 * UPDATED: Multi-step wizard with React Router URL-based navigation and
 * localStorage persistence so users can refresh/go back without losing progress.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Alert,
  TextField,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PaymentIcon from '@mui/icons-material/Payment';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import IndustrySelection from './IndustrySelection';
import SpecializationSelection from './SpecializationSelection';
import TemplateSelection from './TemplateSelection';
import { REQUIRED_TEMPLATE_IDS } from './TemplateSelection';
import LayoutPicker from './LayoutPicker';
import TripleColorPicker from './TripleColorPicker';
import TypographySelector from './TypographySelector';
import AnimationSelector from './AnimationSelector';
import ProfileSelector from './ProfileSelector';
import TemplateCustomizer, { TEMPLATE_LAYOUT_PRESETS } from './TemplateCustomizer';
import ThemePreviewDialog from '../ThemePreviewDialog';
import ThemePurchaseCTA from '../ThemePurchaseCTA';
import { getAllProfiles } from '../../services/profileService';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const steps = [
  'Industry',
  'Branding',
  'Layout',
  'Templates',
  'Customize',
  'Review & Generate'
];

const TOTAL_STEPS = steps.length;

/** localStorage key for persisting wizard state between page loads */
const STORAGE_KEY = 'wordexpress_custom_wizard';

/**
 * Maps TypographySelector style IDs to the actual Google Font names sent to
 * the /api/generate/preview endpoint. Must stay in sync with TypographySelector.jsx.
 */
const TYPOGRAPHY_FONT_MAP = {
  'professional-sans': { heading: 'Inter', body: 'Inter' },
  'elegant-serif': { heading: 'Playfair Display', body: 'Source Sans Pro' },
  'modern-geometric': { heading: 'Montserrat', body: 'Open Sans' },
  'friendly-rounded': { heading: 'Nunito', body: 'Nunito' },
  'classic-traditional': { heading: 'Merriweather', body: 'Lato' }
};

const DEFAULT_TYPOGRAPHY_STYLE = 'professional-sans';

const DEFAULT_FORM_DATA = {
  industry: null,
  specialization: null,
  // Start with the homepage template always included (required)
  selectedPages: ['home'],
  layoutType: 'hero-centered',
  colorScheme: {
    primary: '#0066cc',
    secondary: '#1f2937',
    accent: '#10b981'
  },
  typographyStyle: 'professional-sans',
  animationLevel: 'subtle',
  themeName: '',
  // Per-template customization: keyed by templateId
  // Each entry: { layoutPreset: string|null, sections: [{ sectionId, enabled, order, variant }] }
  templateCustomizations: {}
};

/** Read persisted wizard state from localStorage (returns null if nothing saved) */
const loadPersistedState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Returns true only if a template ID has a known customization entry
 * (i.e. exists in TEMPLATE_LAYOUT_PRESETS). Used to filter out industry
 * page-pool IDs (e.g. mortgage-calculator, agents) that are auto-styled and
 * have no user-configurable options in the Customize step.
 */
const isCustomizableTemplate = (templateId) => templateId in TEMPLATE_LAYOUT_PRESETS;

/**
 * Sanitize a formData object loaded from localStorage.
 * Preserves all selected page IDs (core + any industry-specific ones saved from a
 * previous session with the same industry). Cleans up templateCustomizations for
 * non-customizable IDs so the customizer step stays consistent.
 */
const sanitizeFormData = (data) => {
  if (!data) return data;
  // Keep all page IDs — the backend resolves unknowns gracefully via getPagePool()
  const selectedPages = (data.selectedPages || []).length > 0 ? data.selectedPages : ['home'];
  // Remove templateCustomization entries for non-customizable IDs only
  const templateCustomizations = { ...(data.templateCustomizations || {}) };
  Object.keys(templateCustomizations).forEach((id) => {
    if (!isCustomizableTemplate(id)) {
      delete templateCustomizations[id];
    }
  });
  return { ...data, selectedPages, templateCustomizations };
};

// ---------------------------------------------------------------------------
// Section defaults (mirrors sectionLibrary.js in the backend).
// Used to seed per-template customizations when the Customize step is entered.
// ---------------------------------------------------------------------------

const SECTION_DEFAULTS = {
  home:     [
    { sectionId: 'hero',        enabled: true,  order: 0, defaultVariant: 'centered'   },
    { sectionId: 'features',    enabled: true,  order: 1, defaultVariant: 'card-grid'  },
    { sectionId: 'testimonials',enabled: false, order: 2, defaultVariant: 'card-grid'  },
    { sectionId: 'gallery',     enabled: false, order: 3, defaultVariant: 'uniform-grid'},
    { sectionId: 'stats',       enabled: false, order: 4, defaultVariant: 'horizontal' },
    { sectionId: 'cta',         enabled: true,  order: 5, defaultVariant: 'banner'     },
    { sectionId: 'contact_cta', enabled: false, order: 6, defaultVariant: 'card-strip' },
    { sectionId: 'calculator',  enabled: false, order: 7, defaultVariant: 'full-width', industryRelevant: ['real-estate'] },
    { sectionId: 'agents',      enabled: false, order: 8, defaultVariant: 'card-grid',  industryRelevant: ['real-estate'] },
    { sectionId: 'listings',    enabled: false, order: 9, defaultVariant: 'card-grid',  industryRelevant: ['real-estate'] }
  ],
  about: [
    { sectionId: 'hero',        enabled: true,  order: 0, defaultVariant: 'centered'  },
    { sectionId: 'features',    enabled: false, order: 1, defaultVariant: 'card-grid' },
    { sectionId: 'stats',       enabled: false, order: 2, defaultVariant: 'horizontal'},
    { sectionId: 'testimonials',enabled: false, order: 3, defaultVariant: 'card-grid' },
    { sectionId: 'agents',      enabled: false, order: 4, defaultVariant: 'card-grid', industryRelevant: ['real-estate'] },
    { sectionId: 'cta',         enabled: true,  order: 5, defaultVariant: 'banner'    }
  ],
  services: [
    { sectionId: 'hero',        enabled: true,  order: 0, defaultVariant: 'centered'  },
    { sectionId: 'features',    enabled: true,  order: 1, defaultVariant: 'card-grid' },
    { sectionId: 'calculator',  enabled: false, order: 2, defaultVariant: 'full-width', industryRelevant: ['real-estate'] },
    { sectionId: 'testimonials',enabled: false, order: 3, defaultVariant: 'card-grid' },
    { sectionId: 'cta',         enabled: true,  order: 4, defaultVariant: 'banner'    }
  ],
  contact: [
    { sectionId: 'hero',        enabled: false, order: 0, defaultVariant: 'centered'   },
    { sectionId: 'contact_cta', enabled: true,  order: 1, defaultVariant: 'card-strip' },
    { sectionId: 'agents',      enabled: false, order: 2, defaultVariant: 'card-grid',  industryRelevant: ['real-estate'] }
  ],
  gallery: [
    { sectionId: 'hero',    enabled: false, order: 0, defaultVariant: 'centered'     },
    { sectionId: 'gallery', enabled: true,  order: 1, defaultVariant: 'uniform-grid' },
    { sectionId: 'cta',     enabled: false, order: 2, defaultVariant: 'banner'       }
  ],
  blog: [
    { sectionId: 'hero', enabled: false, order: 0, defaultVariant: 'centered' },
    { sectionId: 'cta',  enabled: false, order: 1, defaultVariant: 'banner'   }
  ],
  single: [
    { sectionId: 'cta', enabled: false, order: 0, defaultVariant: 'banner' }
  ],
  '404': [
    { sectionId: 'cta', enabled: false, order: 0, defaultVariant: 'banner' }
  ],
  search: [
    { sectionId: 'cta', enabled: false, order: 0, defaultVariant: 'banner' }
  ]
};

const TEMPLATE_LAYOUT_PRESET_DEFAULTS = {
  about:    'full-width-story',
  services: 'card-grid',
  contact:  'simple-form',
  gallery:  'masonry',
  blog:     'standard-list',
  single:   'full-width',
  '404':    'minimal',
  search:   'standard'
};

/**
 * Build initial templateCustomizations for a list of selected templates.
 * Filters out industry-specific sections when their industry doesn't match.
 */
const buildDefaultCustomizations = (selectedTemplates, industry) => {
  const result = {};
  for (const templateId of selectedTemplates) {
    const rawSections = SECTION_DEFAULTS[templateId] || [];
    const sections = rawSections
      .filter((entry) => {
        if (!entry.industryRelevant) return true;
        return industry && entry.industryRelevant.includes(industry);
      })
      .map((entry) => ({
        sectionId: entry.sectionId,
        enabled: entry.enabled,
        order: entry.order,
        variant: entry.defaultVariant
      }));

    result[templateId] = {
      layoutPreset: TEMPLATE_LAYOUT_PRESET_DEFAULTS[templateId] || null,
      sections
    };
  }
  return result;
};

/** Persist current wizard state to localStorage */
const persistState = (formData, useProfile, selectedProfileId) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ formData, useProfile, selectedProfileId }));
  } catch {
    // Ignore storage errors (e.g. private browsing quota limits)
  }
};

/** Clear all persisted wizard state */
const clearPersistedState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
};

const IndustryThemeGenerator = () => {
  const navigate = useNavigate();
  const { stepNum } = useParams();
  const { user } = useAuth();
  const themeCredits = user?.themeCredits ?? 0;

  // Derive the 0-based active step from the URL param; clamp to valid range.
  const activeStep = Math.min(Math.max((parseInt(stepNum, 10) || 1) - 1, 0), TOTAL_STEPS - 1);

  // Restore form state from localStorage exactly once on mount using initialiser functions.
  // sanitizeFormData strips any non-customizable page IDs (e.g. stale industry page-pool IDs).
  const [formData, setFormData] = useState(() =>
    sanitizeFormData(loadPersistedState()?.formData ?? DEFAULT_FORM_DATA)
  );
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [generationStatus, setGenerationStatus] = useState('');
  const [generationProgress, setGenerationProgress] = useState(null);
  const [generationResult, setGenerationResult] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [useProfile, setUseProfile] = useState(() => loadPersistedState()?.useProfile ?? false);
  const [selectedProfileId, setSelectedProfileId] = useState(() => loadPersistedState()?.selectedProfileId ?? null);

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const profileIdFromUrl = params.get('profileId');
    if (profileIdFromUrl) {
      setSelectedProfileId(profileIdFromUrl);
      setUseProfile(true);
    }
    // Pass the persisted profileId so loadProfiles does not accidentally
    // override a value that was already restored from localStorage.
    const persistedProfileId = loadPersistedState()?.selectedProfileId ?? null;
    loadProfiles(profileIdFromUrl, persistedProfileId);
  }, []);

  // Persist wizard state to localStorage whenever it changes
  useEffect(() => {
    persistState(formData, useProfile, selectedProfileId);
  }, [formData, useProfile, selectedProfileId]);

  const loadProfiles = async (profileIdFromUrl, persistedProfileId) => {
    try {
      const response = await getAllProfiles();
      const profileList = response.data || [];
      setProfiles(profileList);

      // Only auto-select the default profile if there is no profile already
      // loaded from a URL param or from persisted localStorage state.
      if (!profileIdFromUrl && !persistedProfileId) {
        const defaultProfile = profileList.find(p => p.is_default);
        if (defaultProfile) {
          setSelectedProfileId(String(defaultProfile.id));
          setUseProfile(true);
        }
      }
    } catch (err) {
      console.error('Failed to load profiles:', err);
    }
  };

  /** Navigate to a specific step by 0-based index */
  const goToStep = (stepIndex) => {
    setError(null);
    window.scrollTo(0, 0);
    navigate(`/ai-custom/step/${stepIndex + 1}`);
  };

  const handleNext = () => {
    // Validate current step
    if (activeStep === 0 && !formData.industry) {
      setError('Please select an industry');
      return;
    }
    // Step 1 (Branding): colors and typography have defaults — always valid
    if (activeStep === 2 && !formData.layoutType) {
      setError('Please select a layout');
      return;
    }
    if (activeStep === 3 && formData.selectedPages.length === 0) {
      setError('Please select at least one theme template');
      return;
    }

    // When moving from Templates (step 4) to Customize (step 5), seed defaults
    // for any templates that don't yet have customization data.
    // Also strip any non-customizable IDs so only fully customizable templates
    // appear in the Customize step.
    if (activeStep === 3) {
      const customizablePages = formData.selectedPages.filter(isCustomizableTemplate);
      const existingCustomizations = formData.templateCustomizations || {};
      const defaults = buildDefaultCustomizations(customizablePages, formData.industry);
      const merged = { ...defaults };
      // Preserve any existing customizations the user may have already set
      for (const templateId of Object.keys(existingCustomizations)) {
        if (merged[templateId]) {
          merged[templateId] = existingCustomizations[templateId];
        }
      }
      setFormData(prev => ({
        ...prev,
        selectedPages: customizablePages,
        templateCustomizations: merged
      }));
    }

    goToStep(activeStep + 1);
  };

  const handleBack = () => {
    goToStep(activeStep - 1);
  };

  /** Cancel/Start Over: clear all persisted state and return to step 1 */
  const handleStartOver = () => {
    clearPersistedState();
    setFormData(DEFAULT_FORM_DATA);
    setUseProfile(false);
    setSelectedProfileId(null);
    setError(null);
    setGenerationStatus('');
    setGenerationResult(null);
    navigate('/ai-custom/step/1');
  };

  const handlePreview = async () => {
    // Always preview with at least the homepage template
    const templatesToPreview = formData.selectedPages.length > 0
      ? formData.selectedPages
      : REQUIRED_TEMPLATE_IDS;

    setPreviewError(null);
    setPreviewData(null);
    setPreviewOpen(true);
    setPreviewLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPreviewError('Authentication required. Please log in.');
        setPreviewLoading(false);
        return;
      }

      // Convert selected template IDs to the { filename: boolean } format the API expects
      const pages = {};
      templatesToPreview.forEach((templateId) => {
        pages[templateId] = true;
      });

      const colors = {
        primary: formData.colorScheme.primary,
        secondary: formData.colorScheme.secondary,
        accent: formData.colorScheme.accent
      };

      const fontMap = TYPOGRAPHY_FONT_MAP[formData.typographyStyle] || TYPOGRAPHY_FONT_MAP[DEFAULT_TYPOGRAPHY_STYLE];
      const fonts = { heading: fontMap.heading, body: fontMap.body };

      const response = await fetch('/api/generate/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pages,
          colors,
          fonts,
          profileId: useProfile ? selectedProfileId : null
        })
      });

      if (!response.ok) {
        // Try to surface the backend error message so the user sees actionable info
        let backendMessage = `Preview request failed (HTTP ${response.status})`;
        try {
          const errBody = await response.json();
          backendMessage = errBody.message || errBody.error || backendMessage;
        } catch {
          // ignore JSON parse failure
        }
        throw new Error(backendMessage);
      }

      const data = await response.json();
      if (!data.pages || Object.keys(data.pages).length === 0) {
        throw new Error('No preview templates were generated. Please ensure template files are available.');
      }
      setPreviewData(data);
    } catch (err) {
      console.error('Preview failed:', err);
      setPreviewError(err.message || 'Failed to generate preview. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle retry
  const handleRetry = async (generationId) => {
    try {
      setError(null);
      setGenerating(true);
      setGenerationProgress(null);
      setGenerationStatus('Retrying generation...');
      
      const response = await axios.post(`/api/projects/${generationId}/retry`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      // Start polling for new attempt
      pollGenerationStatus(generationId);
      
    } catch (error) {
      console.error('Retry error:', error);
      setError(`Retry failed: ${error.response?.data?.error || error.message}`);
      setGenerating(false);
    }
  };
  
  // Handle download
  const handleDownload = async (generationId, themeName) => {
    console.log('=== AI CUSTOM THEME DOWNLOAD ===');
    console.log('Generation ID:', generationId);
    console.log('Theme Name:', themeName);
    
    try {
      setDownloading(true);
      
      const response = await axios.get(`/api/download/${generationId}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${themeName || 'theme'}-${generationId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Download completed successfully');
      
    } catch (error) {
      console.error('=== DOWNLOAD ERROR ===', error);
      
      if (error.response && error.response.data instanceof Blob) {
        const errorText = await error.response.data.text();
        setError(`Download failed: ${errorText}`);
      } else {
        setError(`Download failed: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setDownloading(false);
    }
  };

  // Poll for generation status
  // FIXED: Now displays emoji + subtext messages like DatabaseMode
  const pollGenerationStatus = (generationId) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await axios.get(`/api/generate/status/${generationId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        // FIXED: Extract emoji, message, and subtext from response
        const { status, paymentStatus, canDownload, retryCount, validationDetails, emoji, message, subtext, progress, error_message } = statusResponse.data;

        if (status === 'completed') {
          clearInterval(pollInterval);
          setGenerating(false);
          setGenerationProgress(100);
          
          if (paymentStatus === 'paid' && canDownload) {
            // Payment completed - ready to download
            setGenerationStatus('✅ Custom Theme generated and paid!');
            setGenerationResult({ 
              generationId, 
              themeName: formData.themeName,
              canDownload: true,
              paymentStatus: 'paid'
            });
          } else {
            // Generation completed - needs payment
            setGenerationStatus('✅ Custom Theme generated successfully!');
            setGenerationResult({ 
              generationId, 
              themeName: formData.themeName,
              canDownload: false,
              paymentStatus: 'pending'
            });
          }
          
        } else if (status === 'failed') {
          clearInterval(pollInterval);
          setGenerating(false);
          
          // Check if retries are available
          if (retryCount < 3) {
            setError(`Generation failed${error_message ? `: ${error_message}` : ''}. You have ${3 - retryCount} free retries remaining.`);
            setGenerationResult({
              generationId,
              themeName: formData.themeName,
              canRetry: true,
              retryCount
            });
          } else {
            setError('Generation failed after 3 attempts. Please contact support.');
          }
          
        } else {
          // FIXED: Display industry-specific messages with emoji and subtext
          if (progress !== null && progress !== undefined) {
            setGenerationProgress(progress);
          }
          if (emoji && message) {
            const displayMessage = subtext 
              ? `${emoji} ${message}\n${subtext}`
              : `${emoji} ${message}`;
            setGenerationStatus(displayMessage);
          } else {
            // Fallback to generic message if emoji/message not available
            setGenerationStatus(`Generating Custom Theme... Status: ${status}`);
          }
        }
      } catch (err) {
        console.error('Status check error:', err);
      }
    }, 5000);

    // Stop polling after 20 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (generating) {
        setGenerating(false);
        setError('Generation timeout. Please check your generation history.');
      }
    }, 1200000);
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setError(null);
      setGenerationStatus('Starting Custom Theme generation...');
      setGenerationProgress(null);
      setGenerationResult(null);

      // Validate theme name
      if (!formData.themeName) {
        setError('Please enter a theme name');
        setGenerating(false);
        return;
      }

      // Call generation API
      const response = await axios.post('/api/industries/generate', {
        industry: formData.industry,
        specialization: formData.specialization,
        selectedPages: formData.selectedPages,
        layoutType: formData.layoutType,
        colorScheme: formData.colorScheme,
        typographyStyle: formData.typographyStyle,
        animationLevel: formData.animationLevel,
        themeName: formData.themeName,
        profileId: useProfile ? selectedProfileId : null,
        templateCustomizations: formData.templateCustomizations || {}
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.data.success) {
        const generationId = response.data.generationId;
        
        setGenerationStatus('Custom Theme generation in progress... This may take 10-15 minutes.');

        // FIXED: Start polling instead of redirecting
        pollGenerationStatus(generationId);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Generation failed');
      console.error('Generation error:', err);
      setGenerating(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      // Step 1: Industry + Specialization
      case 0:
        return (
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              What industry are you building a theme for?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: themeCredits > 0 ? 2 : 3 }}>
              Your industry shapes the visual style, terminology, and template recommendations
              for your theme.
            </Typography>
            {themeCredits > 0 && (
              <Alert severity="success" sx={{ mb: 3 }}>
                You have <strong>{themeCredits} Studio credit{themeCredits !== 1 ? 's' : ''}</strong> — this theme will use one credit instead of charging you at the end.
              </Alert>
            )}
            <IndustrySelection
              value={formData.industry}
              onChange={(industry) => setFormData(prev => ({
                ...prev,
                industry,
                specialization: null,
                // Clear any industry-specific page IDs from the previous industry
                selectedPages: (prev.selectedPages || []).filter(id => isCustomizableTemplate(id))
              }))}
            />
            <SpecializationSelection
              industry={formData.industry ? { slug: formData.industry } : null}
              value={formData.specialization}
              onChange={(specialization) => setFormData({ ...formData, specialization })}
            />
          </Box>
        );

      // Step 2: Branding (colors + typography)
      case 1:
        return (
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Define your brand style
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose the colors and fonts that represent your brand. These will be applied
              consistently across every template in your theme.
            </Typography>
            <TripleColorPicker
              value={formData.colorScheme}
              onChange={(colorScheme) => setFormData({ ...formData, colorScheme })}
            />
            <TypographySelector
              value={formData.typographyStyle}
              onChange={(typographyStyle) => setFormData({ ...formData, typographyStyle })}
            />
          </Box>
        );

      // Step 3: Homepage Layout
      case 2:
        return (
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Choose your homepage layout
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select the overall structure and hero style for your theme's front page.
            </Typography>
            <LayoutPicker
              value={formData.layoutType}
              onChange={(layoutType) => setFormData({ ...formData, layoutType })}
            />
          </Box>
        );

      // Step 4: Template selection (simplified, industry-standard)
      case 3:
        return (
          <TemplateSelection
            value={formData.selectedPages}
            onChange={(selectedPages) => setFormData({ ...formData, selectedPages })}
            industry={formData.industry}
            specialization={formData.specialization}
          />
        );

      // Step 5: Per-template customization (layout presets + section manager)
      case 4:
        return (
          <TemplateCustomizer
            selectedTemplates={formData.selectedPages}
            industry={formData.industry}
            customizations={formData.templateCustomizations || {}}
            onCustomizationChange={(templateId, updatedConfig) =>
              setFormData(prev => ({
                ...prev,
                templateCustomizations: {
                  ...prev.templateCustomizations,
                  [templateId]: updatedConfig
                }
              }))
            }
          />
        );

      // Step 6: Review & Generate
      case 5:
        return (
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Review &amp; Generate Your Theme
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Give your theme a name, choose an animation style, and optionally use your
              business profile data. Then generate your custom WordPress theme.
            </Typography>

            <AnimationSelector
              value={formData.animationLevel}
              onChange={(animationLevel) => setFormData({ ...formData, animationLevel })}
            />

            <ProfileSelector
              profiles={profiles}
              useProfile={useProfile}
              selectedProfileId={selectedProfileId}
              onUseProfileChange={setUseProfile}
              onProfileChange={setSelectedProfileId}
            />

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Theme Name
              </Typography>
              <TextField
                fullWidth
                value={formData.themeName}
                onChange={(e) => setFormData({ ...formData, themeName: e.target.value })}
                placeholder="Enter your theme name (e.g., My Dental Practice)"
                variant="outlined"
                helperText="This will be the name of your downloadable WordPress theme"
              />
            </Box>

            {/* Generate Button & Status */}
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePreview}
                  disabled={generating || previewLoading}
                  startIcon={previewLoading ? <CircularProgress size={20} color="inherit" /> : <VisibilityIcon />}
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  {previewLoading ? 'Previewing…' : 'Preview Theme'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={handleGenerate}
                  disabled={generating}
                  startIcon={generating && <CircularProgress size={20} color="inherit" />}
                >
                  {generating ? 'Generating...' : 'Generate Custom Theme'}
                </Button>
              </Box>
              
              {error && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {error}
                </Alert>
              )}
              
              {/* Generation Status - Shows emoji + subtext + progress bar */}
              {generationStatus && (
                <Box 
                  sx={{ 
                    mt: 3, 
                    p: 2, 
                    bgcolor: '#f0f9ff', 
                    border: '1px solid #0ea5e9',
                    borderRadius: 2,
                    color: '#0c4a6e'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    {generating && <CircularProgress size={20} thickness={4} />}
                    <Typography 
                      variant="body1"
                      sx={{ 
                        whiteSpace: 'pre-line',
                        fontWeight: 500 
                      }}
                    >
                      {generationStatus}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant={generationProgress !== null ? 'determinate' : 'indeterminate'}
                    value={generationProgress !== null ? generationProgress : undefined}
                    sx={{ borderRadius: 1, height: 6 }}
                  />
                  {generationProgress !== null && (
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                      {generationProgress}%
                    </Typography>
                  )}
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    💡 You'll only be charged if generation succeeds and passes validation
                  </Typography>
                </Box>
              )}
              
              {/* Generation Result - Payment Required */}
              {generationResult && generationResult.paymentStatus === 'pending' && (
                <Box 
                  sx={{ 
                    mt: 3, 
                    p: 3, 
                    bgcolor: '#f0f9ff', 
                    border: '2px solid #16a34a',
                    borderRadius: 2,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h6" color="success.main" gutterBottom>
                    🎉 Custom Theme Generated Successfully!
                  </Typography>
                  
                  <Typography variant="body1" color="textSecondary" paragraph>
                    Your theme "{generationResult.themeName}" passed validation and is ready.
                  </Typography>

                  <Typography variant="body2" color="textSecondary" paragraph>
                    Complete your payment to download your theme.
                  </Typography>
                  
                  <ThemePurchaseCTA
                    projectId={generationResult.generationId}
                    projectMode="custom"
                    paymentStatus={generationResult.paymentStatus}
                    projectStatus="completed"
                    context="generation"
                    onPaymentSuccess={() =>
                      setGenerationResult(prev =>
                        prev ? { ...prev, paymentStatus: 'paid', canDownload: true } : prev
                      )
                    }
                  />
                </Box>
              )}
              
              {/* Generation Result - Ready to Download */}
              {generationResult && generationResult.paymentStatus === 'paid' && generationResult.canDownload && (
                <Box 
                  sx={{ 
                    mt: 3, 
                    p: 3, 
                    bgcolor: '#f0fdf4', 
                    border: '2px solid #16a34a',
                    borderRadius: 2,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h6" color="success.main" gutterBottom>
                    ✅ Payment Confirmed - Ready to Download!
                  </Typography>
                  
                  <Typography variant="body1" color="textSecondary" paragraph>
                    Your Custom Theme "{generationResult.themeName}" is ready.
                  </Typography>
                  
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={() => handleDownload(generationResult.generationId, generationResult.themeName)}
                    disabled={downloading}
                    startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    sx={{ mt: 2 }}
                  >
                    {downloading ? 'Downloading...' : 'Download Your Theme'}
                  </Button>
                </Box>
              )}
              
              {/* Generation Result - Failed with Retry */}
              {generationResult && generationResult.canRetry && (
                <Box 
                  sx={{ 
                    mt: 3, 
                    p: 3, 
                    bgcolor: '#fef2f2', 
                    border: '2px solid #dc2626',
                    borderRadius: 2,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h6" color="error.main" gutterBottom>
                    ⚠️ Generation Failed
                  </Typography>
                  
                  <Typography variant="body1" color="textSecondary" paragraph>
                    No charge applied. You have {3 - generationResult.retryCount} free retries remaining.
                  </Typography>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => handleRetry(generationResult.generationId)}
                    startIcon={<RefreshIcon />}
                    sx={{ mt: 2 }}
                  >
                    Retry Generation (Free)
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 10 }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h3" gutterBottom fontWeight="bold" textAlign="center">
          Build Your Custom WordPress Theme
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
          Build a custom theme in minutes — your actual pages and content are
          created in WordPress Admin after installation.
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && activeStep < TOTAL_STEPS - 1 && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 4, mb: 3, minHeight: 400 }}>
          {renderStepContent(activeStep)}
        </Paper>

        {activeStep < TOTAL_STEPS - 1 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.default',
              py: 2,
              borderTop: 1,
              borderColor: 'divider',
              zIndex: 10
            }}
          >
            <Box display="flex" gap={1}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                size="large"
              >
                Back
              </Button>
              <Button
                color="error"
                variant="text"
                size="large"
                onClick={handleStartOver}
                startIcon={<CancelIcon />}
              >
                Cancel / Start Over
              </Button>
            </Box>
            <Box>
              <Button
                variant="contained"
                size="large"
                onClick={handleNext}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Container>

      {/* Theme Preview Dialog */}
      <ThemePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onGenerate={handleGenerate}
        previewData={previewData}
        previewLoading={previewLoading}
        previewError={previewError}
        title="Custom Theme Preview"
      />
    </Box>
  );
};

export default IndustryThemeGenerator;